import { COMPARE_WINDOWS_BY_WINDOW, CUSTOM_WINDOW } from '../models/alert.constants'
import type { AlertDraft } from '../models/alert.types'
import type { ChannelAvailability } from '../models/channel.types'

export type AlertStepId = 'monitor' | 'filter' | 'conditions' | 'notify' | 'review'

export type AlertValidationKey =
  | 'alerts.validation.name'
  | 'alerts.validation.level'
  | 'alerts.validation.metric'
  | 'alerts.validation.amount'
  | 'alerts.validation.valueType'
  | 'alerts.validation.window'
  | 'alerts.validation.customDays'
  | 'alerts.validation.compareWindow'
  | 'alerts.validation.channel'
  | 'alerts.validation.recipients'

export interface AlertValidationResult {
  ok: boolean
  stepErrors: Record<AlertStepId, AlertValidationKey | null>
  stepErrorKeys: Record<AlertStepId, readonly AlertValidationKey[]>
  firstInvalidStep: AlertStepId | null
  invalidMetricFields: string[]
}

export interface AlertValidationContext {
  channelAvailability?: readonly ChannelAvailability[]
}

const STEP_ORDER: readonly AlertStepId[] = ['monitor', 'filter', 'conditions', 'notify', 'review']

function hasRecipient(values: readonly string[]): boolean {
  return values.some((value) => value.trim().length > 0)
}

function channelError(
  draft: AlertDraft,
  availability: readonly ChannelAvailability[] | undefined,
): AlertValidationKey | null {
  if (!draft.channels.length) return 'alerts.validation.channel'
  if (availability) {
    const selectable = new Set(
      availability.filter((item) => item.selectable).map((item) => item.channel),
    )
    if (draft.channels.some((channel) => !selectable.has(channel))) {
      return 'alerts.validation.channel'
    }
  }
  if (draft.channels.includes('mail') && !hasRecipient(draft.recipientEmails)) {
    return 'alerts.validation.recipients'
  }
  if (draft.channels.includes('whatsapp') && !hasRecipient(draft.whatsappNumbers)) {
    return 'alerts.validation.recipients'
  }
  return null
}

function monitorError(draft: AlertDraft): AlertValidationKey | null {
  if (!draft.name.trim()) return 'alerts.validation.name'
  return draft.level ? null : 'alerts.validation.level'
}

function monitorErrors(draft: AlertDraft): readonly AlertValidationKey[] {
  return [
    ...(!draft.name.trim() ? (['alerts.validation.name'] as const) : []),
    ...(!draft.level ? (['alerts.validation.level'] as const) : []),
  ]
}

function hasValidCustomWindow(draft: AlertDraft): boolean {
  if (draft.window !== 'custom') return true
  const days = draft.customWindowDays
  return (
    days !== null &&
    Number.isInteger(days) &&
    days >= CUSTOM_WINDOW.min &&
    days <= CUSTOM_WINDOW.max
  )
}

function conditionsError(
  draft: AlertDraft,
  invalidMetricFields: string[],
): AlertValidationKey | null {
  if (!draft.metrics.length) return 'alerts.validation.metric'
  for (const metric of draft.metrics) {
    if (!metric.mode) return 'alerts.validation.valueType'
    if (metric.amount === null || !Number.isFinite(metric.amount)) {
      invalidMetricFields.push(metric.column.field)
    }
  }
  if (invalidMetricFields.length) return 'alerts.validation.amount'
  if (!draft.window) return 'alerts.validation.window'
  if (!hasValidCustomWindow(draft)) return 'alerts.validation.customDays'

  const allowed = COMPARE_WINDOWS_BY_WINDOW[
    draft.window
  ] as readonly (typeof draft.metrics)[number]['compareWindow'][]
  const hasInvalidComparison = draft.metrics.some(
    (metric) =>
      metric.mode === 'percentage' &&
      (!metric.compareWindow || !allowed.includes(metric.compareWindow)),
  )
  return hasInvalidComparison ? 'alerts.validation.compareWindow' : null
}

/** Pure wizard validation. Read-only rules retain structural data and validate notifications only. */
export function validateAlertDraft(
  draft: AlertDraft,
  context: AlertValidationContext = {},
): AlertValidationResult {
  const stepErrors: AlertValidationResult['stepErrors'] = {
    monitor: null,
    filter: null,
    conditions: null,
    notify: null,
    review: null,
  }
  const invalidMetricFields: string[] = []
  const monitorErrorKeys = draft.readOnly ? [] : monitorErrors(draft)

  if (!draft.readOnly) {
    stepErrors.monitor = monitorError(draft)
    stepErrors.conditions = conditionsError(draft, invalidMetricFields)
  }

  stepErrors.notify = channelError(draft, context.channelAvailability)
  const firstInvalidStep = STEP_ORDER.find((step) => stepErrors[step] !== null) ?? null
  return {
    ok: firstInvalidStep === null,
    stepErrors,
    stepErrorKeys: {
      monitor: monitorErrorKeys,
      filter: [],
      conditions: stepErrors.conditions ? [stepErrors.conditions] : [],
      notify: stepErrors.notify ? [stepErrors.notify] : [],
      review: [],
    },
    firstInvalidStep,
    invalidMetricFields,
  }
}

export class AlertValidationError extends Error {
  override readonly name = 'AlertValidationError'

  constructor(readonly result: AlertValidationResult) {
    super(result.stepErrors[result.firstInvalidStep ?? 'review'] ?? 'Alert validation failed')
  }
}

export const alertValidator = { validate: validateAlertDraft } as const
